import React, { useState, useEffect, useCallback } from 'react';
import { 
  Key, Plus, Copy, Check, X, Loader2, Eye, EyeOff, 
  Shield, Trash2, RefreshCw, Power, PowerOff, AlertTriangle,
  Search, Filter, ChevronDown, ChevronRight, Clock, Activity
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const IntegrationAPIKeys = () => {
  // États
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // États modaux
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  
  // États formulaires
  const [selectedKey, setSelectedKey] = useState(null);
  const [newKeyData, setNewKeyData] = useState({ name: '', description: '', permissions: [] });
  const [generatedKey, setGeneratedKey] = useState('');
  const [revokeConfirmName, setRevokeConfirmName] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Charger les données
  useEffect(() => {
    loadAPIKeys();
    loadAvailablePermissions();
  }, [statusFilter]);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/settings/integration-api-keys?${params.toString()}`);
      if (response.data.success) {
        setApiKeys(response.data.api_keys);
      }
    } catch (error) {
      console.error('Erreur chargement clés:', error);
      toast.error('Erreur lors du chargement des clés API');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePermissions = async () => {
    try {
      const response = await api.get('/settings/integration-api-keys/available-permissions');
      if (response.data.success) {
        setAvailablePermissions(response.data.modules);
      }
    } catch (error) {
      console.error('Erreur chargement permissions:', error);
    }
  };

  // Handlers
  const handleSearch = useCallback(() => {
    loadAPIKeys();
  }, [searchTerm, statusFilter]);

  const handleCreateKey = async () => {
    if (!newKeyData.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    
    if (newKeyData.permissions.length === 0) {
      toast.error('Sélectionnez au moins une permission');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post('/settings/integration-api-keys', newKeyData);
      
      if (response.data.api_key) {
        setGeneratedKey(response.data.api_key);
        setShowCreateModal(false);
        setShowKeyModal(true);
        toast.success('Clé API créée avec succès');
        loadAPIKeys();
      }
    } catch (error) {
      console.error('Erreur création clé:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableKey = async (keyId) => {
    try {
      await api.post(`/settings/integration-api-keys/${keyId}/disable`);
      toast.success('Clé désactivée');
      loadAPIKeys();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désactivation');
    }
  };

  const handleEnableKey = async (keyId) => {
    try {
      await api.post(`/settings/integration-api-keys/${keyId}/enable`);
      toast.success('Clé réactivée');
      loadAPIKeys();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réactivation');
    }
  };

  const handleRevokeKey = async () => {
    if (revokeConfirmName !== selectedKey?.name) {
      toast.error('Le nom de confirmation ne correspond pas');
      return;
    }

    try {
      setSubmitting(true);
      await api.post(`/settings/integration-api-keys/${selectedKey.id}/revoke`, {
        confirm_name: revokeConfirmName
      });
      toast.success('Clé révoquée définitivement');
      setShowRevokeModal(false);
      setShowDetailModal(false);
      setRevokeConfirmName('');
      loadAPIKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la révocation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerateKey = async () => {
    try {
      setSubmitting(true);
      const response = await api.post(`/settings/integration-api-keys/${selectedKey.id}/regenerate`);
      
      if (response.data.api_key) {
        setGeneratedKey(response.data.api_key);
        setShowRegenerateModal(false);
        setShowDetailModal(false);
        setShowKeyModal(true);
        toast.success('Nouvelle clé générée');
        loadAPIKeys();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la régénération');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Clé copiée dans le presse-papiers');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const togglePermission = (module, action) => {
    setNewKeyData(prev => {
      const permissions = [...prev.permissions];
      const moduleIndex = permissions.findIndex(p => p.module === module);
      
      if (moduleIndex === -1) {
        // Ajouter le module avec l'action
        permissions.push({ module, actions: [action] });
      } else {
        const actions = [...permissions[moduleIndex].actions];
        const actionIndex = actions.indexOf(action);
        
        if (actionIndex === -1) {
          actions.push(action);
        } else {
          actions.splice(actionIndex, 1);
        }
        
        if (actions.length === 0) {
          permissions.splice(moduleIndex, 1);
        } else {
          permissions[moduleIndex] = { ...permissions[moduleIndex], actions };
        }
      }
      
      return { ...prev, permissions };
    });
  };

  const isPermissionSelected = (module, action) => {
    const modulePerm = newKeyData.permissions.find(p => p.module === module);
    return modulePerm?.actions?.includes(action) || false;
  };

  const selectAllForModule = (module) => {
    const moduleData = availablePermissions[module];
    if (!moduleData) return;
    
    const allActions = Object.keys(moduleData.permissions);
    
    setNewKeyData(prev => {
      const permissions = prev.permissions.filter(p => p.module !== module);
      permissions.push({ module, actions: allActions });
      return { ...prev, permissions };
    });
  };

  const selectReadOnly = () => {
    const permissions = Object.keys(availablePermissions).map(module => ({
      module,
      actions: ['read']
    }));
    setNewKeyData(prev => ({ ...prev, permissions }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 hover:bg-green-600">🟢 Active</Badge>;
      case 'disabled':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">🟡 Désactivée</Badge>;
      case 'revoked':
        return <Badge className="bg-red-500 hover:bg-red-600">🔴 Révoquée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const resetCreateForm = () => {
    setNewKeyData({ name: '', description: '', permissions: [] });
  };

  return (
    <div className="p-6 space-y-6" data-testid="integration-api-keys-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="h-6 w-6" />
            Clés API d'Intégration
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez les clés API pour les intégrations externes (scripts, partenaires, automatisations)
          </p>
        </div>
        <Button 
          onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
          data-testid="create-api-key-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle clé
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Rechercher</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Nom de la clé..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="w-48">
              <Label>Statut</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Désactivée</SelectItem>
                  <SelectItem value="revoked">Révoquée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Filter className="h-4 w-4 mr-2" />
              Filtrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des clés */}
      <Card>
        <CardHeader>
          <CardTitle>Clés API ({apiKeys.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucune clé API trouvée</p>
              <Button 
                variant="link" 
                onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
              >
                Créer votre première clé
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div 
                  key={key.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => { setSelectedKey(key); setShowDetailModal(true); }}
                  data-testid={`api-key-item-${key.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{key.name}</span>
                        {getStatusBadge(key.status)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        <code className="bg-gray-100 px-2 py-1 rounded">{key.key_prefix}...</code>
                        <span className="ml-4">{key.permissions_summary}</span>
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Créée le {formatDate(key.created_at)}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Activity className="h-3 w-3" />
                        Dernière utilisation: {formatDate(key.last_used_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Création */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer une nouvelle clé API</DialogTitle>
            <DialogDescription>
              Définissez un nom et sélectionnez les permissions pour cette clé d'intégration.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Nom et description */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="key-name">Nom de la clé *</Label>
                <Input
                  id="key-name"
                  placeholder="Ex: Portail Client - Sync Operations"
                  value={newKeyData.name}
                  onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                  data-testid="key-name-input"
                />
              </div>
              <div>
                <Label htmlFor="key-desc">Description (optionnel)</Label>
                <Input
                  id="key-desc"
                  placeholder="Description de l'usage de cette clé"
                  value={newKeyData.description}
                  onChange={(e) => setNewKeyData({ ...newKeyData, description: e.target.value })}
                />
              </div>
            </div>

            {/* Raccourcis */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectReadOnly}>
                Lecture seule (tous modules)
              </Button>
              <Button variant="outline" size="sm" onClick={() => setNewKeyData({ ...newKeyData, permissions: [] })}>
                Réinitialiser
              </Button>
            </div>

            {/* Sélection des permissions */}
            <div>
              <Label className="mb-3 block">Permissions *</Label>
              <Accordion type="multiple" className="border rounded-lg">
                {Object.entries(availablePermissions).map(([moduleKey, moduleData]) => (
                  <AccordionItem key={moduleKey} value={moduleKey}>
                    <AccordionTrigger className="px-4 hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        <span>{moduleData.label}</span>
                        {newKeyData.permissions.find(p => p.module === moduleKey) && (
                          <Badge variant="secondary" className="ml-2">
                            {newKeyData.permissions.find(p => p.module === moduleKey)?.actions.length || 0}
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="flex justify-end mb-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); selectAllForModule(moduleKey); }}
                        >
                          Tout sélectionner
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(moduleData.permissions).map(([action, description]) => (
                          <div 
                            key={action}
                            className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50"
                          >
                            <Checkbox
                              id={`${moduleKey}-${action}`}
                              checked={isPermissionSelected(moduleKey, action)}
                              onCheckedChange={() => togglePermission(moduleKey, action)}
                            />
                            <label
                              htmlFor={`${moduleKey}-${action}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              <span className="font-medium capitalize">{action}</span>
                              <span className="text-gray-500 ml-2">- {description}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateKey} disabled={submitting} data-testid="confirm-create-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Key className="h-4 w-4 mr-2" />}
              Créer la clé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Détail */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détail de la clé API</DialogTitle>
          </DialogHeader>
          
          {selectedKey && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-lg">{selectedKey.name}</span>
                {getStatusBadge(selectedKey.status)}
              </div>
              
              {selectedKey.description && (
                <p className="text-gray-500">{selectedKey.description}</p>
              )}
              
              <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Préfixe</span>
                  <code className="bg-gray-200 px-2 py-0.5 rounded">{selectedKey.key_prefix}...</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Créée le</span>
                  <span>{formatDate(selectedKey.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Créée par</span>
                  <span>{selectedKey.created_by_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Dernière utilisation</span>
                  <span>{formatDate(selectedKey.last_used_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Nombre d'appels</span>
                  <span>{selectedKey.usage_count || 0}</span>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Permissions</Label>
                <div className="bg-gray-50 p-3 rounded-lg text-sm max-h-40 overflow-y-auto">
                  {selectedKey.permissions?.map((perm, idx) => (
                    <div key={idx} className="mb-2">
                      <span className="font-medium capitalize">{perm.module}</span>: 
                      <span className="text-gray-600 ml-1">{perm.actions?.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {selectedKey?.status === 'active' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => handleDisableKey(selectedKey.id)}
                >
                  <PowerOff className="h-4 w-4 mr-2" />
                  Désactiver
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowRegenerateModal(true)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Régénérer
                </Button>
              </>
            )}
            {selectedKey?.status === 'disabled' && (
              <Button 
                variant="outline" 
                onClick={() => handleEnableKey(selectedKey.id)}
              >
                <Power className="h-4 w-4 mr-2" />
                Réactiver
              </Button>
            )}
            {selectedKey?.status !== 'revoked' && (
              <Button 
                variant="destructive"
                onClick={() => setShowRevokeModal(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Révoquer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Révocation */}
      <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Révoquer la clé "{selectedKey?.name}" ?
            </DialogTitle>
            <DialogDescription className="text-left space-y-2 pt-4">
              <p><strong>Cette action est IRRÉVERSIBLE.</strong></p>
              <p>La clé sera définitivement invalidée. Toutes les intégrations utilisant cette clé cesseront de fonctionner immédiatement.</p>
              <p className="pt-2">Pour confirmer, tapez le nom de la clé ci-dessous :</p>
            </DialogDescription>
          </DialogHeader>
          
          <Input
            placeholder={selectedKey?.name}
            value={revokeConfirmName}
            onChange={(e) => setRevokeConfirmName(e.target.value)}
            className="mt-2"
            data-testid="revoke-confirm-input"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRevokeModal(false); setRevokeConfirmName(''); }}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRevokeKey}
              disabled={submitting || revokeConfirmName !== selectedKey?.name}
              data-testid="confirm-revoke-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Révoquer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Régénération */}
      <Dialog open={showRegenerateModal} onOpenChange={setShowRegenerateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Régénérer la clé "{selectedKey?.name}" ?
            </DialogTitle>
            <DialogDescription className="text-left space-y-2 pt-4">
              <p>Une nouvelle clé sera générée.</p>
              <p><strong>L'ancienne clé cessera de fonctionner immédiatement.</strong></p>
              <p>Les permissions seront conservées.</p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleRegenerateKey} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Régénérer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Affichage Clé (une seule fois) */}
      <Dialog open={showKeyModal} onOpenChange={(open) => { if (!open) setGeneratedKey(''); setShowKeyModal(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              Clé API générée avec succès
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="mb-2 block">Votre clé API :</Label>
            <div className="relative">
              <Input
                readOnly
                value={generatedKey}
                className="pr-12 font-mono text-sm"
                data-testid="generated-key-input"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={() => copyToClipboard(generatedKey)}
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
              <p className="font-medium text-yellow-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                ATTENTION
              </p>
              <p className="text-yellow-700 mt-1">
                Cette clé ne sera plus jamais affichée. Copiez-la et conservez-la dans un endroit sécurisé.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => { setShowKeyModal(false); setGeneratedKey(''); }} data-testid="key-copied-btn">
              J'ai copié ma clé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationAPIKeys;
